import React from "react";
import IframeContent from "./IframeContent";
import { useApplicationContext } from "../context/ApplicationContext";
import { getSessionId, getAuthType } from "../api";

const buildIframeUrl = (baseUrl: string, params: {
  sessionId: string;
  authType: string;
  tenantId?: string;
}): string => {
  const authenticationParams = encodeURI(baseUrl) +
  "&sessionId=" +
  encodeURIComponent(params.sessionId) +
  "&authType=" +
  encodeURIComponent(params.authType) +
  "&tenantId=" +
  encodeURIComponent(params.tenantId);

  return authenticationParams;
};

const buildCleanIframeUrl = (baseUrl: string, params: {
  sessionId: string;
  authType: string;
  tenantId?: string;
}): string => {
  const cleanUrl = baseUrl.replace(/[?&]tenantId=[^&]*/, "");
  const hasQueryParams = cleanUrl.includes("?");
  const separator = hasQueryParams ? "&" : "?";

  const authenticationParams =
    encodeURI(cleanUrl) +
    separator +
    "sessionId=" +
    encodeURIComponent(params.sessionId) +
    "&authType=" +
    encodeURIComponent(params.authType) +
    "&tenantId=" +
    encodeURIComponent(params.tenantId)

  return authenticationParams;
};

// Legacy URLs (rxview/perks) use a semicolon-based parameter format and do not need tenantId.
// All other URLs use the standard query-string format with tenantId included.
const isLegacyUrl = (url: string): boolean => url.includes("rxview") || url.includes("perks");
const isEligibilityUrl = (url: string): boolean => url.includes("rxclaim-nextgen-eligibility");
const getIframeAuthType = (): string => {
  const authType = getAuthType() || "";
  return authType === "testing" ? "msid" : authType;
};
const buildEligibilityIframeUrl = (baseUrl: string, sessionId: string, authType: string): string => {
  const cleanUrl = baseUrl.replace(/[?&]tenantId=[^&]*/, "");
  const hasQueryParams = cleanUrl.includes("?");
  const separator = hasQueryParams ? "&" : "?";
  const standardBase64SessionId = sessionId.replace(/-/g, "+").replace(/_/g, "/");

  return (
    encodeURI(cleanUrl) +
    separator +
    "sessionId=" +
    encodeURIComponent(standardBase64SessionId) +
    "&authType=" +
    encodeURIComponent(authType)
  );
};

const RenderIframe: React.FC = () => {
  const { activeView, tenantId, programData, setProgramData } = useApplicationContext();
  const url = activeView?.url ?? "";
  const isEligibility = isEligibilityUrl(url);

  const handleLoad = (event: any) => {
    try {
      const iframeWindow = event?.target?.contentWindow;
      if (iframeWindow && programData) {
        iframeWindow.postMessage({ type: "set-params", data: programData }, "*");
        setProgramData(null);
      }
    } catch (e) {
      // ignore
    }
  };

  const authParams = {
    sessionId: getSessionId() || '',
    authType: getIframeAuthType(),
    tenantId: tenantId,
  };

  if (isLegacyUrl(url)) {
    const iframeUrl = buildIframeUrl(url, authParams);
    return (
      <IframeContent
        src={iframeUrl}
        id="frame0"
        className="iframe"
        title="Main Application Frame"
      />
    );
  }

  const iframeUrl = isEligibility
    ? buildEligibilityIframeUrl(url, authParams.sessionId, authParams.authType)
    : buildCleanIframeUrl(url, authParams);
  return (
    <IframeContent
      src={iframeUrl}
      id="frame0"
      className="iframe"
      title="Application Frame"
      onLoad={(e: any) => handleLoad(e)}
    />
  );
};

export default RenderIframe;
